import { ref, computed } from "vue";

interface WeeklyRange {
  label: string;
  generator: string;
  actualTodayWeek: number;
  from: string;
  to: string;
}

// Helper: get ISO week number
export const getISOWeek = (d: Date): number => {
  const dateCopy = new Date(d.getTime());
  dateCopy.setHours(0, 0, 0, 0);
  // Thursday in current week decides the year
  dateCopy.setDate(dateCopy.getDate() + 3 - ((dateCopy.getDay() + 6) % 7));
  const week1 = new Date(dateCopy.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((dateCopy.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7
    )
  );
};

// Helper: get date from week number
export const getDateFromWeek = (
  weekNumber: number,
  year: number = new Date().getFullYear()
): Date => {
  // ISO week 1 is the week containing Jan 4
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = (jan4.getDay() + 6) % 7; // Monday = 0
  const target = new Date(jan4);
  target.setDate(jan4.getDate() - dayOfWeek + (weekNumber - 1) * 7);

  // Convert to local ISO string (date only)
  const tzoffset = target.getTimezoneOffset() * 60000; // offset in ms
  const localISOTime = new Date(target.getTime() - tzoffset)
    .toISOString()
    .slice(0, 10); // YYYY-MM-DD
  return new Date(localISOTime);
};

export const useWeeklyDateRange = (weekRangeLength: number = 1) => {
  // Current week state (1-52) - initialize from prop
  const currentWeek = ref<number>(1);
  const baseRangeLength = ref(weekRangeLength);

  // Computed properties for button states
  const isPreviousDisabled = false; // computed(() => currentWeek.value <= 1);
  const isNextDisabled = false; // computed(() => currentWeek.value >= 44); // 52 - 9 + 1 = 44

  const getWeeklyRange = (): WeeklyRange => {
    const startDate = getDateFromWeek(currentWeek.value);
    const endWeek = currentWeek.value + baseRangeLength.value;

    const from = startDate.toISOString().slice(0, 10);
    const to = new Date(getDateFromWeek(endWeek));
    to.setDate(to.getDate() + baseRangeLength.value * 7 - 1);

    // I need to return the actual today week
    const actualTodayWeek = getISOWeek(new Date());

    const endWeekLabel =
      endWeek + baseRangeLength.value > 52
        ? ((endWeek + baseRangeLength.value) % 52) - 1
        : endWeek + baseRangeLength.value - 1;

    return {
      label: `W ${currentWeek.value} - W ${endWeekLabel}`,
      generator: `W${currentWeek.value}-W${endWeekLabel}`,
      actualTodayWeek: actualTodayWeek,
      from: from,
      to: to.toISOString().slice(0, 10),
    };
  };

  const previousWeek = (): void => {
    currentWeek.value--;
  };

  const nextWeek = (): void => {
    currentWeek.value++;
  };

  const resetToCurrentWeek = (): void => {
    currentWeek.value = getISOWeek(new Date());
  };

  return {
    currentWeek,
    isPreviousDisabled,
    isNextDisabled,
    getWeeklyRange,
    previousWeek,
    nextWeek,
    resetToCurrentWeek,
  };
};
